import { type AuthUser, useAuth } from '../../../hooks/use-auth'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import {
  GetOrganizationOptionsForUser,
  GetUserDetails,
  SetDefaultOrganization
} from '../../../services/UserManagementService'
import { useContext, useEffect, useState } from 'react'
import { BreadcrumbNav } from '../../customComponents/BreadcrumbNav'
import { type User } from '../../../models/User'
import { toast } from 'react-toastify'
import { UserDetails } from '../../customComponents/user/Details'
import { MemberOrganizationsList } from './OrganizationsList'
import type { SelectableOrganization } from '../../../models/SelectableOrganization'
import { OrganizationType } from '../../../utils/genericTypes'
import { SetBreadcrumbContext } from '../../../contextProviders/BreadcrumbProvider'
import { generatePath } from 'react-router'
import { AppRoutes } from '../../Routes'

export const UserProfile = (): JSX.Element => {
  const setBreadcrumbs = useContext(SetBreadcrumbContext)
  const { user } = useAuth()
  const [userDetails, setUserDetails] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [orgOptions, setOrgOptions] = useState<SelectableOrganization[]>([])

  const sortOrgs = (orgs: SelectableOrganization[]): SelectableOrganization[] => {
    return orgs.sort((a, b) => {
      if (a.type === b.type) {
        return 0
      }

      if (a.type === OrganizationType.Project ||
          b.type === OrganizationType.District ||
         (a.type === OrganizationType.RCN && b.type === OrganizationType.ISD)) {
        return -1
      }

      return 1
    })
  }

  useEffect(() => {
    setBreadcrumbs([{ name: 'Home', route: generatePath(AppRoutes.HOME) }])
  }, [])

  useEffect(() => {
    const loadUserDetails = async (user: AuthUser | null): Promise<void> => {
      if (user == null) return

      setIsLoading(true)

      const userDetails = await GetUserDetails(user.id.toString())
      setUserDetails(userDetails)

      const orgs = await GetOrganizationOptionsForUser()
      setOrgOptions(sortOrgs(orgs))

      setIsLoading(false)
    }

    void loadUserDetails(user)
  }, [user])

  const handleDefaultOrgClicked = async (organizationId: number): Promise<void> => {
    if (userDetails == null) return

    await SetDefaultOrganization(userDetails.id.toString(), organizationId)
    const tempUserDetails = { ...userDetails }
    tempUserDetails.organizations.forEach((org) => {
      org.isDefault = org.id === organizationId
    })
    setUserDetails(tempUserDetails)
    toast.success('Default organization updated')
  }

  return <Box data-testid='userProfile' sx={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
    <BreadcrumbNav />

    <Typography variant='h1' data-testid='pageHeader'>User Profile</Typography>

    {isLoading && <CircularProgress />}

    {!isLoading && userDetails != null && (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1em', justifyContent: 'center' }}>
      <Grid container spacing={2}>
        <Grid item sx={{ ml: '2em', mt: '2em' }}>
          <UserDetails
            users={[userDetails]}
            imageKey={userDetails.imageKey}
            detailName={userDetails.fullName}
            isOrganizationView={false}
            isEditable={true}
            onUpdate={setUserDetails}
            expanded={true}
            hideExpand={true}
            allowEditEmail={false}
          />
        </Grid>
        <Grid item sx={{ mt: '2em' }} data-testid='userOrganizations'>
          <MemberOrganizationsList
            organizations={userDetails.organizations}
            orgOptions={orgOptions}
            readOnly={true}
            handleDefaultOrgClicked={handleDefaultOrgClicked}
            handleAddNewOrg={() => {}}
            handleEditOrg={() => {}}
            handleRemoveOrg={() => {}}
            handleBuildingsEdit={() => {}}
            handleSubscriptionsEdit={() => {}}
            expanded={true}
            hideExpand={true}
            hideBuildings={true}
            hideRoles={true}
            allowDefaultOrgUpdate={true}
          />
        </Grid>
      </Grid>
    </Box>)}

  </Box>
}
